// Countdowns (design 4 §5.2): base time, then the bank, on a turn; a call window, a pre-discard or a
// last-tile offer runs on the base time only (owner decision 2026-10-10: the bank is public, so it is
// never spent on a call).

import { PromptKind, type Prompt } from "../pb/mj/protocol/v1/protocol_pb";
import { ActionType } from "../pb/mj/record/v1/record_pb";

export function isTurn(kind: PromptKind): boolean {
  return kind === PromptKind.AFTER_DRAW || kind === PromptKind.RIICHI_DISCARD || kind === PromptKind.AFTER_CLAIM;
}

/** "base+bank" seconds while the base runs, then the bank's; `since` is when the values were received. */
export function secondsLeft(base: number, bank: number, since: number, now: number): string {
  const spent = now - since;
  const b = Math.max(0, base - spent);
  const k = Math.max(0, bank - Math.max(0, spent - base));
  return b > 0 ? `${Math.ceil(b / 1000)}+${Math.ceil(k / 1000)}` : `${Math.ceil(k / 1000)}`;
}

/** The seat's own countdown for its open prompt. */
export function promptSecondsLeft(p: Prompt, since: number, now: number): string {
  if (isTurn(p.kind)) return secondsLeft(p.baseMsLeft, p.bankMsLeft, since, now);
  return `${Math.ceil(Math.max(0, p.baseMsLeft - (now - since)) / 1000)}`;
}

/** How long the call buttons stay live at a table without assists, from when a discard reaches the seat (mjcore/server/table.h: the server
 *  moves on a little later, so that a press made while they are live arrives in time). */
export const CALL_SHOWN_MS = 2_000;

/** Whether a button can be pressed now, which says nothing of the hand: a call on a discard while its window shows, the seat's own on its turn. */
export function buttonLive(types: readonly ActionType[], ownTurn: boolean, callsOpen: boolean): boolean {
  return types.some((type) => (type === ActionType.TSUMO || type === ActionType.DECLARE_RIICHI || type === ActionType.CLOSED_KAN || type === ActionType.ADDED_KAN ? ownTurn : callsOpen));
}

/** The seconds of warning before a clock runs out, each marked. */
export const LOW_SECONDS = 5;

/** How long from `now` until each of the clock's last seconds begins; those already gone are not marked. A turn's only: an offer left to
 *  run out is only a pass, with nothing lost, and one is open at every other discard. */
export function lowTimeMarks(p: Prompt, since: number, now: number): number[] {
  const total = p.baseMsLeft + p.bankMsLeft;
  if (!isTurn(p.kind) || !total) return [];
  const left = total - (now - since);
  const out: number[] = [];
  for (let s = LOW_SECONDS; s >= 1; s--) if (left >= s * 1000) out.push(left - s * 1000);
  return out;
}

/** The own clock in its parts, whole seconds: the base while it runs (null once spent) and the bank (null where
 * the bank is never spent: a call window, a pre-discard, a last-tile offer). */
export interface ClockParts {
  base: number | null;
  bank: number | null;
}

export function promptClock(p: Prompt, since: number, now: number): ClockParts {
  const spent = now - since;
  const base = Math.max(0, p.baseMsLeft - spent);
  if (!isTurn(p.kind)) return { base: Math.ceil(base / 1000), bank: null };
  const bank = Math.max(0, p.bankMsLeft - Math.max(0, spent - p.baseMsLeft));
  return { base: base > 0 ? Math.ceil(base / 1000) : null, bank: Math.ceil(bank / 1000) };
}
